import { useState, useEffect } from 'react';
import { getFromPrice } from '../utils/unitAvailability';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { sanitizeHtml } from '../utils/sanitizeHtml';
import VideoSection from '../components/shared/VideoSection';
import NativeVideoPlayer from '../components/shared/NativeVideoPlayer';
import InvestmentSummaryForm from '../components/forms/InvestmentSummaryForm';
import FamilyPtyVideoForm from '../components/familyPty/FamilyPtyVideoForm';
import PropertyCard from '../components/PropertyCard';
import SectionHeading from '../components/shared/SectionHeading';
import CreditScoreSection from '../components/shared/CreditScoreSection';
import TestimonialsSection from '../components/shared/TestimonialsSection';
import CallbackBookingSection from '../components/shared/CallbackBookingSection';
import NewsletterSection from '../components/shared/NewsletterSection';
import AppPromoStrip from '../components/shared/AppPromoStrip';
import SEOHead from '../components/shared/SEOHead';
import Reveal from '../components/shared/Reveal';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { getFeaturedDevelopments } from '../services/propertyService';
import { HOME_PAGE, ABOUT_PAGE } from '../data/pageContent';
import { useWebsiteContent } from '../contexts/WebsiteContentContext';
import { resolveVideoProps } from '../utils/videoProps';
import type { Development } from '../../../src/types/property.types';

export default function HomePage() {
  const cms = useWebsiteContent();
  const home = cms?.pages.home ?? HOME_PAGE;
  const about = cms?.pages.about ?? ABOUT_PAGE;
  const pageVideos = cms?.pageVideos ?? {};
  const pageVideoEntries = cms?.pageVideoEntries;
  // New key (2026-09-18): saved CMS docs predating it fall through to code.
  const strategy = home.strategy ?? HOME_PAGE.strategy;
  const strategyVideo = resolveVideoProps('homeWhyInvest', pageVideos, pageVideoEntries);
  const navigate = useNavigate();
  const featuredGrid = useScrollReveal<HTMLDivElement>({ threshold: 0.15 });
  const [featured, setFeatured] = useState<Development[]>([]);
  const [loadingFeatured, setLoadingFeatured] = useState(true);

  useEffect(() => {
    const loadFeatured = async () => {
      try {
        const devs = await getFeaturedDevelopments();
        setFeatured(devs);
      } catch (error) {
        console.error('Error loading featured developments:', error);
      } finally {
        setLoadingFeatured(false);
      }
    };
    loadFeatured();
  }, []);

  const getMinPrice = (dev: Development) => getFromPrice(dev.units) ?? 0;

  return (
    <>
      <SEOHead
        title="PropInvest&#8482; | South Africa's #1 Property Investment Company"
        description="South Africa's #1 property investment company. Exclusive deals, education and free tools to achieve financial freedom and build generational wealth."
        path="/"
        // Organization schema is injected globally by TrackingScripts
        // (ORGANIZATION_SCHEMA in utils/seo.ts) \u2014 do not duplicate it here.
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "name": "PropInvest",
          "url": "https://propinvest.co.za",
          "description": "Exclusive property investments, education and tools to achieve financial freedom and build generational wealth in South Africa.",
          "areaServed": "ZA",
          "priceRange": "R500,000 - R5,000,000",
          "@id": "https://propinvest.co.za/#business"
        }}
      />

      {/* H1 — primary page heading */}
      <section className="section-padding pb-0 bg-white">
        <div className="section-container text-center">
          <Reveal variant="up">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 tracking-tight">
              South Africa&apos;s #1 Property Investment Platform
            </h1>
            <div className="mt-3 h-1 w-16 bg-primary rounded mx-auto" />
          </Reveal>
        </div>
      </section>

      {/* About Us */}
      <section className="section-padding bg-white">
        <div className="section-container">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">
            <Reveal variant="left">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">ABOUT US</h2>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(about.paragraph1) }} />
                <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(about.paragraph2) }} />
              </div>
            </Reveal>
            <VideoSection {...resolveVideoProps('about', pageVideos, pageVideoEntries)} />
          </div>
        </div>
      </section>

      {/* 1. Free, custom wealth creation strategy (was "All the services you
          need", 2026-09-18). Text column left, video (CMS slot
          homeAllServices) right; "Get Now" scrolls to the strategy form in
          the next section. Wording is CMS-editable under pages.home.strategy. */}
      <section className="section-padding bg-white">
        <div className="section-container">
          <VideoSection
            title={strategy.heading}
            description={strategy.subtext}
            linkTo={strategy.link}
            linkText="Get Now"
            {...resolveVideoProps('homeAllServices', pageVideos, pageVideoEntries)}
            reversed
          />
        </div>
      </section>

      {/* 1b. Group Millionaire Strategy (Jaco 2026-10-12, public Family Pty
          launch): the /family-pty "How Family Pty works" video + brochure
          button | family_pty form, straight from Website CMS → Family Pty
          (same component as the page). Leads are Family Pty leads (Cindy's
          Group script). "Get Now" above still scrolls to #summary-form. */}
      <section className="section-padding bg-white pt-0">
        <div className="section-container">
          <FamilyPtyVideoForm calculatorHref="/family-pty/calculator" />
        </div>
      </section>

      {/* 2. Strategy video + Millionaire Strategy form (2026-09-18) — the
          "Why invest" heading/text/button column became the same
          InvestmentSummaryForm as /millionaire-strategy and /education
          (formContext 'millionaire_strategy' — leads report under that source
          by design). The video is the CMS slot homeWhyInvest (an uploaded MP4
          plays through the native player spec; a YouTube ID keeps the lazy
          facade). */}
      <section
        id="summary-form"
        className="section-padding bg-gradient-to-br from-gray-200 via-gray-100 to-gray-200 scroll-mt-16"
      >
        <div className="section-container">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">
            <Reveal variant="scale">
              {strategyVideo.videoUrl ? (
                <NativeVideoPlayer src={strategyVideo.videoUrl} poster={strategyVideo.thumbnailUrl} />
              ) : (
                <VideoSection videoId={strategyVideo.videoId} />
              )}
            </Reveal>
            <Reveal variant="up">
              <InvestmentSummaryForm formContext="millionaire_strategy" />
            </Reveal>
          </div>
        </div>
      </section>

      {/* 3. Testimonials */}
      <TestimonialsSection />

      {/* 4. App Promotion */}
      <AppPromoStrip />

      {/* 5. Featured Investment Opportunities */}
      {!loadingFeatured && featured.length > 0 && (
        <section className="section-padding bg-white">
          <div className="section-container">
            <Reveal variant="up">
              <SectionHeading title="Featured Investment Opportunities" />
            </Reveal>
            <div ref={featuredGrid.ref} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {featured.map((dev, i) => (
                <div
                  key={dev.id}
                  className={`reveal ${featuredGrid.isVisible ? 'visible' : ''} reveal-delay-${Math.min(i + 1, 8)}`}
                >
                  <PropertyCard
                    id={dev.id}
                    name={dev.name}
                    facilitator={dev.facilitator}
                    marketingName={dev.marketingName}
                    city={dev.city || dev.province}
                    province={dev.province}
                    propertyType="Residential"
                    imageUrl={dev.marketingAssets?.heroImage || dev.marketingAssets?.headerImage}
                    images={dev.marketingAssets?.galleryImages}
                    minPrice={getMinPrice(dev)}
                    isFeatured
                    onClick={() => navigate(`/property/${dev.slug || dev.id}`)}
                  />
                </div>
              ))}
            </div>
            <Reveal variant="up" className="text-center mt-8">
              <Link
                to="/best-property-investment-opportunities"
                className="inline-flex items-center gap-2 text-primary hover:text-primary-dark font-semibold transition"
              >
                View All Property Investment Deals
                <ArrowRight size={18} />
              </Link>
            </Reveal>
          </div>
        </section>
      )}

      {/* Loading skeleton for featured */}
      {loadingFeatured && (
        <section className="section-padding bg-white">
          <div className="section-container">
            <div className="h-8 w-72 bg-gray-200 rounded-lg animate-pulse mx-auto mb-8" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map(i => (
                <div key={i} className="aspect-[4/3] bg-gray-200 rounded-xl animate-pulse" />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* 6. Free Instant Credit Check */}
      <CreditScoreSection />

      {/* 7. Book a Free Callback */}
      <CallbackBookingSection />

      {/* 8. Newsletter */}
      <NewsletterSection />
    </>
  );
}
